import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Activity, TestTube, Heart, Settings, Bell, Menu, X, Smartphone, Ticket, Gauge } from 'lucide-react';
import clsx from 'clsx';
import ApiStatus from './ApiStatus';
import TestRunButton from './TestRunButton';
import TestCaseSearch from './TestCaseSearch';

const Layout = ({ children }) => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Activity },
    { name: 'Test Runs', href: '/test-runs', icon: TestTube },
    { name: 'Jira Tickets', href: '/jira-tickets', icon: Ticket },
    { name: 'Health', href: '/health', icon: Heart },
    { name: 'Page Speed', href: '/performance', icon: Gauge },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50/80">
      {/* Top nav */}
      <nav className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-14 gap-4">
            <div className="flex items-center min-w-0">
              {/* Brand */}
              <Link to="/" className="flex items-center gap-2 mr-4 lg:mr-8 shrink-0">
                <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center">
                  <TestTube className="h-4 w-4 text-white" />
                </div>
                <span className="text-base font-semibold text-gray-900 hidden sm:block">
                  Bajaao QA
                </span>
              </Link>

              {/* Desktop nav */}
              <div className="hidden md:flex gap-1">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={clsx(
                        'flex items-center gap-1.5 px-2.5 lg:px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap shrink-0',
                        isActive(item.href)
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="hidden lg:block min-w-0 flex-1">
                <TestCaseSearch />
              </div>
              <TestRunButton />
              <button
                className="md:hidden p-2 rounded-md text-gray-500 hover:bg-gray-100"
                onClick={() => setMobileOpen(!mobileOpen)}
              >
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white px-4 py-2 space-y-1">
            <div className="sm:hidden pb-2">
              <TestCaseSearch />
            </div>
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={clsx(
                    'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium',
                    isActive(item.href)
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {item.name}
                </Link>
              );
            })}
          </div>
        )}
      </nav>

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {children}
      </main>

      {/* Minimal footer */}
      <footer className="border-t border-gray-200/60 bg-white/60 px-4 py-2">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <span className="text-xs text-gray-400">bajaao.com test suite</span>
          <ApiStatus />
        </div>
      </footer>
    </div>
  );
};

export default Layout;
